/**
 * Frontend observability bootstrap. Initialises Sentry when REACT_APP_SENTRY_DSN
 * is present, and stamps every axios request with an X-Correlation-ID header so
 * that traces can be tied between frontend and backend logs.
 */
import api from './api';

let _correlationCounter = 0;
function newCorrelationId() {
  _correlationCounter += 1;
  const ts = Date.now().toString(36);
  const rnd = Math.random().toString(36).slice(2, 8);
  return `web-${ts}-${rnd}-${_correlationCounter}`;
}

export function initObservability() {
  // ── Correlation ID interceptor ─────────────────────────────
  api.interceptors.request.use((config) => {
    if (!config.headers['X-Correlation-ID']) {
      config.headers['X-Correlation-ID'] = newCorrelationId();
    }
    return config;
  });

  // ── Sentry (optional) ───────────────────────────────────────
  // We deliberately do NOT bundle @sentry/react at build time. To enable
  // Sentry in production:
  //   1. Add the official browser snippet to public/index.html, e.g.
  //      <script src="https://browser.sentry-cdn.com/.../bundle.tracing.min.js"></script>
  //   2. Set window.SENTRY_DSN (or REACT_APP_SENTRY_DSN at build time).
  // This keeps the build optional while still wiring init when present.
  const dsn = process.env.REACT_APP_SENTRY_DSN || (typeof window !== 'undefined' && window.SENTRY_DSN);
  if (!dsn) return;
  try {
    const Sentry = (typeof window !== 'undefined' && window.Sentry) || null;
    if (Sentry && typeof Sentry.init === 'function') {
      Sentry.init({
        dsn,
        environment: process.env.REACT_APP_ENV || 'production',
        tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.1),
        replaysSessionSampleRate: Number(process.env.REACT_APP_SENTRY_SESSION_SAMPLE_RATE || 0),
        replaysOnErrorSampleRate: 1.0,
      });
      // eslint-disable-next-line no-console
      console.info('[obs] Sentry initialised via browser snippet');
    } else {
      // eslint-disable-next-line no-console
      console.warn('[obs] SENTRY_DSN set but Sentry browser snippet was not loaded.');
    }
  } catch (_) {
    // Silent failure; observability must never block rendering.
  }
}

export default initObservability;
